import type { NoticeProduct, PurposeSelection } from "../api/types";
import { CONFIRM_HINT_COPY, PURPOSE_STATUS_ACTIVE } from "../constants";
import type {
  ConfirmVerdict,
  PurposeRow,
  RecordedLookup,
  ScopedPurpose,
  SelectablePurpose,
  SelectionState,
  SubmissionPurpose,
} from "../types";
import {
  gatedProductUuids,
  noticeScopesPurposes,
  productPurposeKey,
  purposeNamesProduct,
  rowProductUuid,
  serverMarksMandatoryProducts,
} from "../core/productMatrix";
import {
  dataElementKey,
  hasRequiredDataElement,
  isBlockingRequiredElement,
  seededDataElements,
} from "./consentSelection";

const rowKey = (row: PurposeRow) =>
  productPurposeKey(row.productUuid, row.purpose.uuid);

export const recordedSelectionLookup =
  (
    purposeSelections: Record<string, PurposeSelection> | undefined,
    productPurposeSelections:
      | Record<string, Record<string, PurposeSelection>>
      | undefined,
  ): RecordedLookup =>
  (purposeUuid, productUuid) => {
    const perProduct = productUuid
      ? productPurposeSelections?.[productUuid]?.[purposeUuid]
      : undefined;
    return perProduct ?? purposeSelections?.[purposeUuid];
  };

export const seedSelection = (
  rows: ReadonlyArray<PurposeRow>,
  recordedFor: RecordedLookup,
): SelectionState => {
  const selectedPurposes: Record<string, boolean> = {};
  const selectedDataElements: Record<string, boolean> = {};

  rows.forEach((row) => {
    const recorded = recordedFor(row.purpose.uuid, row.productUuid);
    selectedPurposes[rowKey(row)] = recorded ? recorded.selected : false;
    row.purpose.data_elements.forEach((element) => {
      selectedDataElements[
        dataElementKey(row.purpose.uuid, element.uuid, row.productUuid)
      ] = recorded?.data_elements?.[element.uuid]?.selected ?? false;
    });
  });

  return { selectedPurposes, selectedDataElements };
};

export const collapseEveryRow = (
  rows: ReadonlyArray<PurposeRow>,
): Record<string, boolean> =>
  rows.reduce((acc: Record<string, boolean>, row) => {
    acc[rowKey(row)] = true;
    return acc;
  }, {});

export const initialCollapsedProducts = (
  groups: ReadonlyArray<{ product: { uuid: string } }>,
  defaultOpenProducts?: ReadonlyArray<string>,
): Record<string, boolean> => {
  const openByDefault = new Set(defaultOpenProducts ?? []);
  const soleSection = groups.length === 1;
  return groups.reduce((acc: Record<string, boolean>, group) => {
    acc[group.product.uuid] =
      !soleSection && !openByDefault.has(group.product.uuid);
    return acc;
  }, {});
};

export const recordedBaseline = (
  rows: ReadonlyArray<PurposeRow>,
  recordedFor: RecordedLookup,
): Record<string, boolean> =>
  rows.reduce((acc: Record<string, boolean>, row) => {
    const recorded = recordedFor(row.purpose.uuid, row.productUuid);
    acc[rowKey(row)] =
      recorded?.selected === true &&
      (!recorded.status || recorded.status === PURPOSE_STATUS_ACTIVE) &&
      recorded.needs_reconsent !== true;
    return acc;
  }, {});

export const settleProducts = (
  rows: ReadonlyArray<PurposeRow>,
  products: ReadonlyArray<NoticeProduct> | undefined,
  recordedFor: RecordedLookup,
): {
  consentedProducts: Record<string, boolean>;
  consentedPurposes: Record<string, boolean>;
} => {
  const productList = products ? [...products] : undefined;
  const isSettled = (row: PurposeRow) => {
    const recorded = recordedFor(row.purpose.uuid, row.productUuid);
    return recorded?.selected === true && recorded.needs_reconsent !== true;
  };

  const consentedPurposes: Record<string, boolean> = {};
  const rowsByProduct = new Map<string, PurposeRow[]>();
  rows.forEach((row) => {
    const resolved = rowProductUuid(row, productList);
    if (!resolved) return;
    consentedPurposes[rowKey(row)] = isSettled(row);
    const group = rowsByProduct.get(resolved) ?? [];
    group.push(row);
    rowsByProduct.set(resolved, group);
  });

  const consentedProducts: Record<string, boolean> = {};
  rowsByProduct.forEach((group, productUuid) => {
    const mandatory = group.filter((row) => hasRequiredDataElement(row.purpose));
    const deciding = mandatory.length > 0 ? mandatory : group;
    consentedProducts[productUuid] = deciding.every(isSettled);
  });

  return { consentedProducts, consentedPurposes };
};

export const isFullyConsented = (
  rows: ReadonlyArray<PurposeRow>,
  recordedFor: RecordedLookup,
): boolean =>
  rows.every((row) => {
    const recorded = recordedFor(row.purpose.uuid, row.productUuid);
    return Boolean(recorded?.selected && !recorded?.needs_reconsent);
  });

export const productBoxPurposes = <P extends SelectablePurpose>(
  purposes: ReadonlyArray<P>,
): P[] => {
  const required = purposes.filter(hasRequiredDataElement);
  return required.length > 0 ? required : [...purposes];
};

export const purposeToggle = (
  purpose: SelectablePurpose,
  productUuid: string | undefined,
  selectedPurposes: Record<string, boolean>,
): SelectionState => {
  const key = productPurposeKey(productUuid, purpose.uuid);
  const nextState = !selectedPurposes[key];
  const seeded = seededDataElements(purpose);
  const selectedDataElements: Record<string, boolean> = {};
  purpose.data_elements.forEach((element) => {
    selectedDataElements[`${key}-${element.uuid}`] =
      nextState && seeded.has(element.uuid);
  });
  return { selectedPurposes: { [key]: nextState }, selectedDataElements };
};

export const sectionToggle = (
  sectionPurposes: ReadonlyArray<SelectablePurpose>,
  keyProductUuid: string | undefined,
  selectedPurposes: Record<string, boolean>,
  next?: boolean,
): SelectionState => {
  const nextState =
    next ??
    !sectionPurposes.every(
      (purpose) => selectedPurposes[productPurposeKey(keyProductUuid, purpose.uuid)],
    );
  const purposeOverrides: Record<string, boolean> = {};
  const elementOverrides: Record<string, boolean> = {};
  sectionPurposes.forEach((purpose) => {
    const key = productPurposeKey(keyProductUuid, purpose.uuid);
    purposeOverrides[key] = nextState;
    // The same elements the purpose's own tick grants.
    const seeded = seededDataElements(purpose);
    purpose.data_elements.forEach((element) => {
      elementOverrides[`${key}-${element.uuid}`] =
        nextState && seeded.has(element.uuid);
    });
  });
  return {
    selectedPurposes: purposeOverrides,
    selectedDataElements: elementOverrides,
  };
};

export const purposeCheckedFromElements = (
  purpose: SelectablePurpose,
  productUuid: string | undefined,
  selectedDataElements: Record<string, boolean>,
): boolean => {
  const key = productPurposeKey(productUuid, purpose.uuid);
  const requiredElements = purpose.data_elements.filter(isBlockingRequiredElement);
  return requiredElements.length > 0
    ? requiredElements.every((element) => selectedDataElements[`${key}-${element.uuid}`])
    : purpose.data_elements.some((element) => selectedDataElements[`${key}-${element.uuid}`]);
};

export const confirmVerdict = ({
  products,
  purposes,
  rows,
  selectedPurposes,
  recordedPurposeSelections,
}: {
  products: ReadonlyArray<NoticeProduct> | undefined;
  purposes: ReadonlyArray<ScopedPurpose>;
  rows: ReadonlyArray<PurposeRow>;
  selectedPurposes: Record<string, boolean>;
  recordedPurposeSelections: Record<string, boolean>;
}): ConfirmVerdict => {
  if (rows.length === 0) return { ok: true, reason: "ok", blocking: [] };

  const productList = products ? [...products] : undefined;
  const isSelected = (row: PurposeRow) => Boolean(selectedPurposes[rowKey(row)]);
  const wasRecorded = (row: PurposeRow) =>
    Boolean(recordedPurposeSelections[rowKey(row)]);
  const isMandatory = (row: PurposeRow) => hasRequiredDataElement(row.purpose);
  const satisfies = (group: ReadonlyArray<PurposeRow>) =>
    group.filter(isMandatory).every(isSelected);
  const touched = (group: ReadonlyArray<PurposeRow>) =>
    group.some(isSelected) &&
    group.some((row) => isSelected(row) !== wasRecorded(row));

  const rowsByProduct = new Map<string, PurposeRow[]>();
  const unresolved: PurposeRow[] = [];
  rows.forEach((row) => {
    const productUuid = rowProductUuid(row, productList);
    if (productUuid === undefined) {
      unresolved.push(row);
      return;
    }
    const group = rowsByProduct.get(productUuid);
    if (group) {
      group.push(row);
    } else {
      rowsByProduct.set(productUuid, [row]);
    }
  });

  if (!serverMarksMandatoryProducts(productList)) {
    const groups = [...rowsByProduct.values()];
    if (unresolved.length > 0) groups.push(unresolved);
    const everyProductHasMandatory = groups.every((group) => group.some(isMandatory));
    const baseline = everyProductHasMandatory ? groups.some(satisfies) : satisfies(rows);
    const blocking = [...rowsByProduct.entries()]
      .filter(([, group]) => touched(group) && !satisfies(group))
      .map(([productUuid]) => productUuid);
    const ok = baseline && blocking.length === 0;
    return {
      ok,
      reason: ok
        ? "ok"
        : blocking.length > 0
          ? "product-incomplete"
          : "required-outstanding",
      blocking,
    };
  }

  if (unresolved.length > 0 && rowsByProduct.size === 0) {
    const ok = satisfies(unresolved);
    return { ok, reason: ok ? "ok" : "required-outstanding", blocking: [] };
  }

  const gated = gatedProductUuids(productList);
  const scoped = noticeScopesPurposes([...purposes]);
  const engagingRows = (group: PurposeRow[], productUuid: string) =>
    scoped
      ? group.filter((row) => purposeNamesProduct(row.purpose, productUuid))
      : group;

  const engaged: string[] = [];
  rowsByProduct.forEach((group, productUuid) => {
    if (gated.has(productUuid)) {
      engaged.push(productUuid);
      return;
    }
    if (touched(engagingRows(group, productUuid))) engaged.push(productUuid);
  });

  if (engaged.length === 0) {
    return {
      ok: false,
      reason: rows.some(isSelected) ? "nothing-engaged" : "nothing-chosen",
      blocking: [],
    };
  }

  const blocking = engaged.filter(
    (productUuid) => !satisfies(rowsByProduct.get(productUuid) ?? []),
  );

  return {
    ok: blocking.length === 0,
    reason: blocking.length === 0 ? "ok" : "product-incomplete",
    blocking,
  };
};

export const confirmHint = (
  verdict: ConfirmVerdict,
  products: ReadonlyArray<NoticeProduct> | undefined,
  productName: (product: NoticeProduct) => string,
  copy: typeof CONFIRM_HINT_COPY = CONFIRM_HINT_COPY,
): string | null => {
  if (verdict.ok || verdict.reason === "ok") return null;
  if (verdict.reason !== "product-incomplete") {
    return copy[verdict.reason];
  }
  const byUuid = new Map((products ?? []).map((product) => [product.uuid, product]));
  const names = verdict.blocking.map((uuid) => {
    const product = byUuid.get(uuid);
    return product ? productName(product) : uuid;
  });
  return copy.productsIncomplete(names);
};

export const submissionPurposes = <P extends ScopedPurpose>(
  rows: ReadonlyArray<PurposeRow<P>>,
  selection: SelectionState,
): SubmissionPurpose<P>[] =>
  rows.map(({ purpose, productUuid }) => {
    const key = productPurposeKey(productUuid, purpose.uuid);
    return {
      ...purpose,
      ...(productUuid ? { product_uuid: productUuid } : {}),
      selected: selection.selectedPurposes[key] || false,
      data_elements: purpose.data_elements.map((element) => ({
        ...element,
        selected: selection.selectedDataElements[`${key}-${element.uuid}`] || false,
      })),
    };
  });
